import theme from 'components/theme'
import { actions, selectors } from 'data'
import React, { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { CSSTransition } from 'react-transition-group'
import { isCompact, stripUnit } from 'utils'
import { keys, match } from 'utils/keyboard'

import ApiKey from '../Account/ApiKey'
import ChangeEmail from '../Account/ChangeEmail'
import ChangePassword from '../Account/ChangePassword'
import Deposit from '../Cashflow/Deposit'
import Withdrawal from '../Cashflow/Withdrawal'
import LanguageSelection from '../LanguageSelection'
import CloseMarginPosition from '../Margin/CloseMarginPosition'
import MarginOnboarding from '../Margin/Onboarding'
import MarginPairAnnouncement from '../Margin/PairAnnouncement'
import ResetPassword from '../ResetPassword'
import SignoutSessions from '../SignoutSessions'
import TierTwo from '../TierTwo'
import OrderConfirmation from '../Trade/OrderConfirmation'
import OrderDetails from '../Trade/OrderDetails'
import TwoFactor from '../TwoFactor'
import Modal from './template'
import WhatsNew from './WhatsNew'
import WhitelistAddress from './WhitelistAddress'

type ModalProps = {
  Component
  background?: string
  closeButtonColor: 'white' | 'teal600'
}

const MODALS: Record<string, ModalProps> = {
  API_KEY_ACTIVATE: {
    Component: ApiKey,
    closeButtonColor: 'teal600'
  },
  API_KEY_ACTIVATE_FAIL: {
    Component: ApiKey,
    closeButtonColor: 'teal600'
  },
  API_KEY_CREATE: {
    Component: ApiKey,
    closeButtonColor: 'teal600'
  },
  API_KEY_DELETE: {
    Component: ApiKey,
    closeButtonColor: 'teal600'
  },
  API_KEY_SUCCESS: {
    Component: ApiKey,
    closeButtonColor: 'teal600'
  },
  BACKUP_CODES_TWO_FACTOR: {
    Component: TwoFactor,
    closeButtonColor: 'teal600'
  },
  CHANGE_EMAIL: {
    Component: ChangeEmail,
    closeButtonColor: 'teal600'
  },
  CHANGE_PASSWORD: {
    Component: ChangePassword,
    closeButtonColor: 'teal600'
  },
  CLOSE_MARGIN: {
    Component: CloseMarginPosition,
    background: 'dark700',
    closeButtonColor: 'white'
  },
  DEPOSIT: { Component: Deposit, background: 'dark800', closeButtonColor: 'teal600' },
  DISABLE_TWO_FACTOR: {
    Component: TwoFactor,
    closeButtonColor: 'teal600'
  },
  ENABLE_TWO_FACTOR: {
    Component: TwoFactor,
    closeButtonColor: 'teal600'
  },
  ENABLE_WHITELIST_ADDRESS: {
    Component: WhitelistAddress,
    closeButtonColor: 'teal600'
  },
  LANGUAGE_SELECTION: {
    Component: LanguageSelection,
    closeButtonColor: 'white'
  },
  MARGIN_ONBOARDING: {
    Component: MarginOnboarding,
    closeButtonColor: 'white'
  },
  NEW_MARGIN_PAIR: {
    Component: MarginPairAnnouncement,
    closeButtonColor: 'teal600'
  },
  ORDER_CONFIRMATION: {
    Component: OrderConfirmation,
    closeButtonColor: 'teal600'
  },
  ORDER_DETAILS: {
    Component: OrderDetails,
    closeButtonColor: 'teal600'
  },
  RESET_PASSWORD: {
    Component: ResetPassword,
    closeButtonColor: 'teal600'
  },
  SIGNOUT_SESSIONS: {
    Component: SignoutSessions,
    closeButtonColor: 'teal600'
  },
  TIER_TWO: { Component: TierTwo, background: 'dark800', closeButtonColor: 'teal600' },
  VERIFICATION_DEPOSIT: {
    Component: Deposit,
    closeButtonColor: 'teal600'
  },
  WHATS_NEW: {
    Component: WhatsNew,
    closeButtonColor: 'teal600'
  },
  WITHDRAWAL: {
    Component: Withdrawal,
    background: 'dark800',
    closeButtonColor: 'teal600'
  }
}
const EmptyModal = () => <div />
const DEFAULT: ModalProps = {
  Component: EmptyModal,
  background: undefined,
  closeButtonColor: 'teal600'
}

type PropsType = ConnectedProps<typeof connector>

const ModalContainer = (props: PropsType) => {
  const { fullscreen, hideModal, isDismissable, largescreen, name, screenWidth } = props

  const handleKeyDown = (e: KeyboardEvent) => {
    if (match(e, keys.ESC)) {
      e.stopPropagation()
      if (name && isDismissable) {
        hideModal(name)
      }
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [name])

  const [isClosing, setClosing] = useState(false)

  const closeModal = () => {
    if (name && isDismissable) {
      hideModal(name)
    }
  }

  const handleClickOut = (event) => {
    setClosing(true)
    setTimeout(() => {
      event.preventDefault()
      closeModal()
    }, 0)
  }

  const modalVisible = !!name
  const { Component, background }: ModalProps = name ? MODALS[name] : DEFAULT
  const smallScreen = isCompact(screenWidth)

  return (
    <CSSTransition
      appear
      classNames='fade'
      in={modalVisible}
      timeout={stripUnit(theme.transition.medium)}
      unmountOnExit
    >
      <Modal
        background={background}
        fullscreen={fullscreen || smallScreen}
        isDismissable={isDismissable}
        largescreen={largescreen}
        onClickOut={handleClickOut}
        onClose={closeModal}
      >
        <Component modalName={name} isClosing={isClosing} />
      </Modal>
    </CSSTransition>
  )
}

const mapStateToProps = (state) => ({
  fullscreen: selectors.app.getModalFullscreen(state),
  isDismissable: selectors.app.getModalDismissable(state),
  largescreen: selectors.app.getModalLargescreen(state),
  name: selectors.app.getModalName(state),
  screenWidth: selectors.app.getScreenWidth(state)
})

const mapDispatchToProps = (dispatch) => ({
  hideModal: (name) => {
    dispatch(actions.app.hideModal(name))
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(ModalContainer)
